/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-overview-scrollable-grid.helper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { orderBy } from '@progress/kendo-data-query';
import moment from 'moment';
import { IExtendedDataSetFields } from '@components/central-monitoring/site-dashboard-summary-v2/site-dashboard-summary-v2.interface';
import { createDatePipe, getFilterType } from '@components/central-monitoring/study-summary/study-summary.helper';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '@components/core/grid-filters';
import { FieldDefinitionFieldType } from 'types/graphql-global-types';
import { useGridCells } from '@hooks/index';
import Constants from '@utils/constants';
import { convertYesNoToBoolean } from '@utils/convertor';
import { getCellNumberFromString, getChangeMovement } from '@utils/grid-v2';
import { isStringNumeric, validOverviewDate } from '@utils/validation';
import { DataSetFields } from '@queries/generated/fragments.generated';
import {
    studyOverviewTable_OpraQueries_studyOverviewTable_StudySummaryOverviewTable as StudySummaryOverviewTable,
    studyOverviewTable_OpraQueries_studyOverviewTable_StudySummaryOverviewTable_panelExtraColumns_SummaryOverviewColumnType as panelExtraColumns,
} from '@queries/generated/studies.generated';

const { MaxLengthCell, MovementCell, CustomizableCell, DateCell, YesNoCell } = useGridCells();

export const getObjValueByPath = (path: string, data: any, type?: string | null) => {
    const value = path.split('.').reduce((o, i) => o?.[i], data);

    if (!!type && isStringNumeric(value)) {
        return type === FieldDefinitionFieldType.INT
            ? Number(value)
            : type === FieldDefinitionFieldType.FLOAT
              ? Number(value)?.toFixed(2)
              : value;
    }

    return isStringNumeric(value) ? Number(value)?.toFixed(2) : value;
};

const renderCell = (path: string, dataItem: any) => getObjValueByPath(path, dataItem);

const getDateFormat = (field: string) =>
    moment(field, 'DD-MMM-YYYY', true).isValid()
        ? 'DD-MMM-YYYY'
        : moment(field, 'MMM-YYYY', true).isValid()
          ? 'MMM-YYYY'
          : null;

const getColumnFieldValue = (fieldValue: any, columnType: string | null | undefined, isAbs?: boolean) => {
    if (isStringNumeric(fieldValue)) {
        return getCellNumberFromString(fieldValue, columnType, isAbs);
    }

    if (fieldValue && validOverviewDate(fieldValue)) {
        return createDatePipe(fieldValue);
    }

    if (columnType === Constants.FieldDefinitionFieldTextTypes.bool) {
        return convertYesNoToBoolean(fieldValue);
    }

    if (
        (!fieldValue && columnType === Constants.FieldDefinitionFieldTextTypes.text) ||
        fieldValue === '0.00' ||
        !fieldValue
    ) {
        return '';
    }

    return fieldValue;
};

export const formStudyOverviewData = (studyOverview: StudySummaryOverviewTable[]) =>
    studyOverview.map((row) => {
        const gridData: any = {};
        gridData.siteToShow = true; // We need it to show movement icons

        if (row.columnSummary?.length) {
            row.columnSummary.forEach((col, index) => {
                const columnType = col?.type;
                if (col) {
                    gridData[`summaryCol${index}`] = {
                        columnType,
                        current: getColumnFieldValue(col.current, columnType),
                        ...(col.current &&
                            validOverviewDate(col.current) && {
                                currentDateFormat: getDateFormat(col.current),
                            }),
                        ...(col.previous == null && isStringNumeric(col.current)
                            ? {}
                            : {
                                  previous: getColumnFieldValue(col.previous, columnType),
                                  ...(col.previous &&
                                      validOverviewDate(col.previous) && {
                                          previousDateFormat: getDateFormat(col.previous),
                                      }),
                              }),
                        ...(col.change == null && isStringNumeric(col.current)
                            ? {}
                            : {
                                  change: getColumnFieldValue(col.change, columnType, true),
                              }),
                        movement: getChangeMovement(col.previous, col.current, col.change, columnType),
                    };
                }
            });
        }

        if (row.panelColumnXAxis && row.panelColumnYAxis) {
            const panelColumnsTypeX = row.panelColumnXAxis.type;
            const panelColumnsTypeY = row.panelColumnYAxis.type;

            gridData.panelColumns = {
                panelColumnXAxis: {
                    type: row.panelColumnXAxis.title ?? '',
                    columnType: panelColumnsTypeX,
                    movement: getChangeMovement(
                        row.panelColumnXAxis.previous,
                        row.panelColumnXAxis.current,
                        row.panelColumnXAxis.change,
                        panelColumnsTypeX,
                    ),
                    ...(row.panelColumnXAxis.current != null
                        ? { current: getCellNumberFromString(row.panelColumnXAxis.current, panelColumnsTypeX) }
                        : {}),
                    ...(row.panelColumnXAxis.previous != null
                        ? { previous: getCellNumberFromString(row.panelColumnXAxis.previous, panelColumnsTypeX) }
                        : {}),
                    ...(row.panelColumnXAxis.change != null
                        ? { change: getCellNumberFromString(row.panelColumnXAxis.change, panelColumnsTypeX, true) }
                        : {}),
                },
                panelColumnYAxis: {
                    type: row.panelColumnYAxis.title ?? '',
                    columnType: panelColumnsTypeY,
                    movement: getChangeMovement(
                        row.panelColumnYAxis.previous,
                        row.panelColumnYAxis.current,
                        row.panelColumnYAxis.change,
                        panelColumnsTypeY,
                    ),
                    ...(row.panelColumnYAxis.current != null
                        ? { current: getCellNumberFromString(row.panelColumnYAxis.current, panelColumnsTypeY) }
                        : {}),
                    ...(row.panelColumnYAxis.previous != null
                        ? { previous: getCellNumberFromString(row.panelColumnYAxis.previous, panelColumnsTypeY) }
                        : {}),
                    ...(row.panelColumnYAxis.change != null
                        ? { change: getCellNumberFromString(row.panelColumnYAxis.change, panelColumnsTypeY, true) }
                        : {}),
                },
            };
        }

        if (row.panelExtraColumns?.length) {
            row.panelExtraColumns.forEach((col, index) => {
                const columnType = col?.type;
                if (col) {
                    gridData.panelColumns[`panelExtraColumn${index}`] = {
                        type: col.title,
                        columnType,
                        movement: getChangeMovement(col.previous, col.current, col.change, columnType),
                        ...(col.current != null ? { current: getCellNumberFromString(col.current, columnType) } : {}),
                        ...(col.previous != null
                            ? { previous: getCellNumberFromString(col.previous, columnType) }
                            : {}),
                        ...(col.change != null
                            ? { change: getCellNumberFromString(col.change, columnType, true) }
                            : {}),
                    };
                }
            });
        }

        return {
            panel: row.panel,
            ...gridData,
        };
    });

const getExtraColumns = (
    studyOverview: StudySummaryOverviewTable[],
    previousDataSet: DataSetFields,
    currentDataSet: IExtendedDataSetFields | null | undefined,
    translate: (value: string) => string,
): IExpandableGridColumnV2[] => {
    const getColumns = (
        colsNum: number,
        extraColumns?: ((panelExtraColumns | null)[] | null)[] | (panelExtraColumns | null)[] | null,
    ) => {
        const prevDatasetTooltip = previousDataSet?.isDefault
            ? translate('cm:noCdSelected')
            : previousDataSet?._dataCutDate;
        const prevDatasetTitle = previousDataSet?.isDefault ? ' ' : previousDataSet?._dataCutDate;
        const cols: IExpandableGridColumnV2[] = [];

        for (let i = 0; i < colsNum; i++) {
            const columnType = extraColumns && extraColumns[i]?.type;

            cols.push(
                {
                    field: `panelColumns.panelExtraColumn${i}.type`,
                    id: `extra-column${i}-type`,
                    title: `${translate('cm:companionColumn')} ${i + 1}`,
                    groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:companionColumn')} ${i + 1}`,
                    filter: 'text',
                    minWidth: 150,
                    isChild: true,
                    hideColumnLock: true,
                },
                {
                    field: `panelColumns.panelExtraColumn${i}.current`,
                    id: `extra-column${i}-current`,
                    title: translate('cm:selectedDatasetAbbreviation'),
                    tooltipValue: currentDataSet?._dataCutDate,
                    groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:companionColumn')} ${
                        i + 1
                    } - ${currentDataSet?._dataCutDate}`,
                    filter: 'numeric',
                    cells: {
                        data: CustomizableCell((dataItem: any) =>
                            getObjValueByPath(`panelColumns.panelExtraColumn${i}.current`, dataItem, columnType),
                        ),
                    },
                    show: true,
                    minWidth: 150,
                    isChild: true,
                    hideColumnLock: true,
                },
                {
                    field: `panelColumns.panelExtraColumn${i}.previous`,
                    id: `extra-column${i}-previous`,
                    title: translate('cm:comparisonDatasetAbbreviation'),
                    tooltipValue: prevDatasetTooltip,
                    groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:companionColumn')} ${
                        i + 1
                    } - ${prevDatasetTitle}`,
                    filter: 'numeric',
                    cells: {
                        data: CustomizableCell((dataItem: any) =>
                            getObjValueByPath(`panelColumns.panelExtraColumn${i}.previous`, dataItem, columnType),
                        ),
                    },
                    show: true,
                    minWidth: 150,
                    isChild: true,
                    hideColumnLock: true,
                },
                {
                    field: `panelColumns.panelExtraColumn${i}.change`,
                    id: `extra-column${i}-change`,
                    title: translate('cm:changeAbbreviation'),
                    tooltipValue: translate('ra:change'),
                    groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:companionColumn')} ${
                        i + 1
                    } - ${translate('ra:change')}`,
                    filter: 'numeric',
                    cells: { data: MovementCell(`panelColumns.panelExtraColumn${i}.movement`, true) },
                    show: true,
                    minWidth: 150,
                    isChild: true,
                    hideColumnLock: true,
                },
            );
        }

        return cols;
    };
    const extraColumns: IExpandableGridColumnV2[] = [];
    const oneExtraColumn = studyOverview.find((item) => item.panelExtraColumns?.length === 1);
    const twoExtraColumns = studyOverview.find((item) => item.panelExtraColumns?.length === 2);
    const showOneExtraColumn = !!oneExtraColumn;
    const showTwoExtraColumns = !!twoExtraColumns;
    if (showTwoExtraColumns) {
        const extraCols = twoExtraColumns.panelExtraColumns;
        extraColumns.push(...getColumns(2, extraCols));
    } else if (showOneExtraColumn) {
        const extraCols = [oneExtraColumn.panelExtraColumns];
        extraColumns.push(...getColumns(1, extraCols));
    }

    return extraColumns;
};

export const getStudyOverviewColumns = (
    studyOverview: StudySummaryOverviewTable[],
    previousDataSet: IExtendedDataSetFields,
    currentDataSet: IExtendedDataSetFields | null | undefined,
    translate: (value: string) => string,
): IExpandableGridColumnV2[] => {
    const columns: IExpandableGridColumnV2[] = [
        {
            label: translate('cm:PANEL'),
            id: 'study-overview-panel',
            field: 'panel',
            minWidth: 150,
            lockedByDefault: true,
            cells: { data: MaxLengthCell(150) },
            order: 0,
        },
    ];
    const extraColumns = getExtraColumns(studyOverview, previousDataSet, currentDataSet, translate);
    const prevDatasetTooltip = previousDataSet?.isDefault
        ? translate('cm:noCdSelected')
        : previousDataSet?._dataCutDate;
    const prevDatasetTitle = previousDataSet?.isDefault ? ' ' : previousDataSet?._dataCutDate;

    if (studyOverview?.[0]?.columnSummary?.length) {
        studyOverview[0].columnSummary.forEach((col, index) => {
            if (col) {
                const columnType = col?.type;
                const filter = getFilterType(columnType || '');

                columns.push({
                    id: `study-overview-score-${index}`,
                    field: `summaryCol${index}`,
                    label: col.title || undefined,
                    minWidth: 249,
                    headerClass: 'parent',
                    notFilterable: true,
                    notSortable: true,
                    order: col.columnOrder ?? 9999,
                    children: [
                        {
                            field: `summaryCol${index}.current`,
                            id: `summary-col-current-${index}`,
                            title: translate('cm:selectedDatasetAbbreviation'),
                            tooltipValue: currentDataSet?._dataCutDate,
                            groupTitle: `${col.title || ''} - ${currentDataSet?._dataCutDate}`,
                            filter,
                            ...(col.current && !isStringNumeric(col.current) && validOverviewDate(col.current)
                                ? {
                                      cells: { data: (params: any) => DateCell({ ...params, renderCell }) },
                                      filterUi: GridColumnDateFilter,
                                  }
                                : columnType === Constants.FieldDefinitionFieldTextTypes.bool
                                  ? {
                                        cells: { data: (params: any) => YesNoCell({ ...params, renderCell }) },
                                        filterUi: GridColumnMenuActiveFilter,
                                    }
                                  : {
                                        cells: {
                                            data: CustomizableCell((dataItem: any) =>
                                                getObjValueByPath(`summaryCol${index}.current`, dataItem, columnType),
                                            ),
                                        },
                                    }),
                            minWidth: 90,
                        },
                        {
                            field: `summaryCol${index}.previous`,
                            id: `summary-col-previous-${index}`,
                            title: translate('cm:comparisonDatasetAbbreviation'),
                            tooltipValue: prevDatasetTooltip,
                            groupTitle: `${col.title || ''} - ${prevDatasetTitle}`,
                            filter,
                            ...(col.previous && !isStringNumeric(col.previous) && validOverviewDate(col.previous)
                                ? {
                                      cells: { data: (params: any) => DateCell({ ...params, renderCell }) },
                                      filterUi: GridColumnDateFilter,
                                  }
                                : columnType === Constants.FieldDefinitionFieldTextTypes.bool
                                  ? {
                                        cells: { data: (params: any) => YesNoCell({ ...params, renderCell }) },
                                        filterUi: GridColumnMenuActiveFilter,
                                    }
                                  : {
                                        cells: {
                                            data: CustomizableCell((dataItem: any) =>
                                                getObjValueByPath(`summaryCol${index}.previous`, dataItem, columnType),
                                            ),
                                        },
                                    }),
                            minWidth: 90,
                        },
                        {
                            field: `summaryCol${index}.change`,
                            id: `summary-col-change-${index}`,
                            title: translate('cm:changeAbbreviation'),
                            tooltipValue: translate('ra:change'),
                            groupTitle: `${col.title || ''} - ${translate('ra:change')}`,
                            filter,
                            minWidth: 90,
                            ...(isStringNumeric(col.current)
                                ? { cells: { data: MovementCell(`summaryCol${index}.movement`, true) } }
                                : {}),
                        },
                    ],
                });
            }
        });
    }

    columns.push({
        id: 'study-overview-panel-columns',
        field: 'panelColumns',
        label: translate('cm:panelColumnsLabel'),
        minWidth: 249,
        headerClass: 'parent',
        notFilterable: true,
        hideColumnLock: true,
        notSortable: true,
        order: studyOverview?.[0]?.panelColumnXAxis?.columnOrder ?? 9999,
        children: [
            {
                id: 'column-y-axis',
                field: 'panelColumnYAxis',
                title: translate('cm:countYAxis'),
                groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countYAxis')}`,
                minWidth: 249,
                isChild: true,
                hideColumnLock: true,
                headerClass: 'parent',
                notFilterable: true,
                notSortable: true,
                children: [
                    {
                        field: 'panelColumns.panelColumnYAxis.type',
                        id: 'y-axis-type',
                        title: translate('cm:typeLabel'),
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countYAxis')} - ${translate(
                            'cm:typeLabel',
                        )}`,
                        filter: 'text',
                        show: true,
                        isChild: true,
                        hideColumnLock: true,
                        minWidth: 150,
                    },
                    {
                        field: 'panelColumns.panelColumnYAxis.current',
                        id: 'y-axis-current',
                        title: translate('cm:selectedDatasetAbbreviation'),
                        tooltipValue: currentDataSet?._dataCutDate,
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate(
                            'cm:countYAxis',
                        )} - ${currentDataSet?._dataCutDate}`,
                        filter: 'numeric',
                        cells: {
                            data: CustomizableCell((dataItem: any) =>
                                getObjValueByPath(
                                    'panelColumns.panelColumnYAxis.current',
                                    dataItem,
                                    studyOverview?.[0]?.panelColumnYAxis?.type,
                                ),
                            ),
                        },
                        show: true,
                        isChild: true,
                        hideColumnLock: true,
                        minWidth: 90,
                    },
                    {
                        field: 'panelColumns.panelColumnYAxis.previous',
                        id: 'y-axis-previous',
                        title: translate('cm:comparisonDatasetAbbreviation'),
                        tooltipValue: prevDatasetTooltip,
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate(
                            'cm:countYAxis',
                        )} - ${prevDatasetTitle}`,
                        filter: 'numeric',
                        cells: {
                            data: CustomizableCell((dataItem: any) =>
                                getObjValueByPath(
                                    'panelColumns.panelColumnYAxis.previous',
                                    dataItem,
                                    studyOverview?.[0]?.panelColumnYAxis?.type,
                                ),
                            ),
                        },
                        show: true,
                        isChild: true,
                        hideColumnLock: true,
                        minWidth: 90,
                    },
                    {
                        field: 'panelColumns.panelColumnYAxis.change',
                        id: 'y-axis-change',
                        title: translate('cm:changeAbbreviation'),
                        tooltipValue: translate('ra:change'),
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countYAxis')} - ${translate(
                            'ra:change',
                        )}`,
                        filter: 'numeric',
                        cells: { data: MovementCell('panelColumns.panelColumnYAxis.movement', true) },
                        show: true,
                        isChild: true,
                        hideColumnLock: true,
                        minWidth: 90,
                    },
                ],
            },
            {
                id: 'column-x-axis',
                field: 'panelColumnXAxis',
                title: translate('cm:countXAxis'),
                groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countXAxis')}`,
                minWidth: 249,
                isChild: true,
                hideColumnLock: true,
                headerClass: 'parent',
                notFilterable: true,
                notSortable: true,
                children: [
                    {
                        field: 'panelColumns.panelColumnXAxis.type',
                        id: 'x-axis-type',
                        title: translate('cm:typeLabel'),
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countXAxis')} - ${translate(
                            'cm:typeLabel',
                        )}`,
                        filter: 'text',
                        show: true,
                        minWidth: 150,
                        isChild: true,
                        hideColumnLock: true,
                    },
                    {
                        field: 'panelColumns.panelColumnXAxis.current',
                        id: 'x-axis-current',
                        title: translate('cm:selectedDatasetAbbreviation'),
                        tooltipValue: currentDataSet?._dataCutDate || '',
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate(
                            'cm:countXAxis',
                        )} - ${currentDataSet?._dataCutDate}`,
                        filter: 'numeric',
                        cells: {
                            data: CustomizableCell((dataItem: any) =>
                                getObjValueByPath(
                                    'panelColumns.panelColumnXAxis.current',
                                    dataItem,
                                    studyOverview?.[0]?.panelColumnXAxis?.type,
                                ),
                            ),
                        },
                        show: true,
                        minWidth: 90,
                        isChild: true,
                        hideColumnLock: true,
                    },
                    {
                        field: 'panelColumns.panelColumnXAxis.previous',
                        id: 'x-axis-previous',
                        title: translate('cm:comparisonDatasetAbbreviation'),
                        tooltipValue: prevDatasetTooltip,
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate(
                            'cm:countXAxis',
                        )} - ${prevDatasetTitle}`,
                        filter: 'numeric',
                        cells: {
                            data: CustomizableCell((dataItem: any) =>
                                getObjValueByPath(
                                    'panelColumns.panelColumnXAxis.previous',
                                    dataItem,
                                    studyOverview?.[0]?.panelColumnXAxis?.type,
                                ),
                            ),
                        },
                        show: true,
                        minWidth: 90,
                        isChild: true,
                        hideColumnLock: true,
                    },
                    {
                        field: 'panelColumns.panelColumnXAxis.change',
                        id: 'x-axis-change',
                        title: translate('cm:changeAbbreviation'),
                        tooltipValue: translate('ra:change'),
                        groupTitle: `${translate('cm:panelColumnsLabel')} - ${translate('cm:countXAxis')} - ${translate(
                            'ra:change',
                        )}`,
                        filter: 'numeric',
                        cells: { data: MovementCell('panelColumns.panelColumnXAxis.movement', true) },
                        show: true,
                        minWidth: 90,
                        isChild: true,
                        hideColumnLock: true,
                    },
                ],
            },
            ...extraColumns,
        ],
    });

    return orderBy(columns, [{ field: 'order', dir: 'asc' }]);
};
